// src/pages/MyTicketsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../lib/AuthContext';
import { getUserInterests, getUserRegistrations, getEvent } from '../lib/db';
import BottomNav from '../components/layout/BottomNav';
import { PageBack } from '../components/shared/PageBack';

const fmtDate = (date) => {
  if (!date) return '';
  try {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'EEE d MMM · HH:mm').replace(' · 00:00', '');
  } catch { return ''; }
};

const MyTicketsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState([]);
  const [interested, setInterested] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!user) { setLoading(false); return; }
      setLoading(true);

      const [interestEventIds, registrations] = await Promise.all([
        getUserInterests(user.uid).catch(err => { console.error('getUserInterests failed:', err); return []; }),
        getUserRegistrations(user.uid).catch(err => { console.error('getUserRegistrations failed:', err); return []; }),
      ]);

      // Registered events — fetch each event's current details (title/date may have changed)
      const registeredEvents = (await Promise.all(
        registrations.map(async (r) => {
          const event = await getEvent(r.eventId).catch(() => null);
          return event ? { ...event, registeredAt: r.createdAt } : null;
        })
      )).filter(Boolean);

      // Interested events — same idea, resolve each id to the live event
      const interestedEvents = (await Promise.all(
        interestEventIds.map(id => getEvent(id).catch(() => null))
      )).filter(Boolean);

      // If someone both registered AND marked interested on the same event,
      // show it once under Registered — that's the stronger signal.
      const registeredIds = new Set(registeredEvents.map(e => e.id));
      const interestedOnly = interestedEvents.filter(e => !registeredIds.has(e.id));

      setRegistered(registeredEvents);
      setInterested(interestedOnly);
      setLoading(false);
    };
    load();
  }, [user?.uid]);

  const totalCount = registered.length + interested.length;

  const EventRow = ({ event, badge }) => (
    <button
      onClick={() => navigate(`/event/${event.id}`)}
      style={{ width: '100%', textAlign: 'left', background: 'var(--white)', border: 'none', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', cursor: 'pointer', display: 'block' }}
    >
      <span style={{ background: badge === 'Registered' ? 'var(--sage-deep)' : 'var(--sage-pale)', color: badge === 'Registered' ? 'white' : 'var(--sage-dark)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>
        {badge}
      </span>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginTop: 8, marginBottom: 4 }}>{event.title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
        {fmtDate(event.nextDate || event.date)}{event.venue ? ` · ${event.venue}` : ''}
      </div>
    </button>
  );

  if (!user) {
    return (
      <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 20px' }}>
        <PageBack fallbackPath="/profile" />
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 24, letterSpacing: '-0.3px' }}>Events of Interest</h1>
        <div className="empty-state">
          <div className="empty-state-icon">🎟️</div>
          <div className="empty-state-title">Sign in to see your events</div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 100px' }}>
      <PageBack fallbackPath="/profile" />
      <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 24, letterSpacing: '-0.3px' }}>Events of Interest</h1>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>
      ) : totalCount === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎟️</div>
          <div className="empty-state-title">Nothing here yet</div>
          <div className="empty-state-body">Register for an event or mark one Interested, and it'll show up here.</div>
        </div>
      ) : (
        <>
          {registered.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
                Registered ({registered.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {registered.map(event => <EventRow key={event.id} event={event} badge="Registered" />)}
              </div>
            </div>
          )}

          {interested.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
                Interested ({interested.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {interested.map(event => <EventRow key={event.id} event={event} badge="Interested" />)}
              </div>
            </div>
          )}
        </>
      )}

      <BottomNav />
    </div>
  );
};

export default MyTicketsPage;
